import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { FIELD_LABEL } from '@/constants/field-classes';
import { CountryCodeEnum } from '@/enums/country-code-enum';
import { useCountryCode } from '@/hooks/use-country-code';
import { bai_jamjuree } from '@/public/assets/fonts/Bai-Jamjuree';

// Bone of the skeleton. Same material as the app's other loading states
// (src/components/dashboard/stat-card.tsx, src/layouts/dashboard-sidebar.tsx,
// src/components/blog/post-card-skeleton.tsx). No radius baked in: each use needs a different one, and
// `rounded` plus `rounded-[10px]` in the same string is a utility conflict resolved by source order.
const BONE = 'animate-pulse bg-white/10';

// Chrome copied verbatim from Dialog.Panel in sign-up-modal.tsx, minus `gtm-sign-up` (analytics does not
// belong on a placeholder) and minus `transition-all` (nothing transitions here). Being identical is what
// makes the skeleton-to-panel swap invisible, and what makes this cover the sign-in panel that is fading
// out behind it.
const PANEL = 'relative flex w-full max-w-[480px] flex-col gap-5 overflow-hidden rounded-2xl border border-white/10 bg-[#0f1215] bg-[linear-gradient(135deg,rgba(156,255,46,0.07),rgba(255,255,255,0.02)_48%,rgba(46,230,255,0.05))] p-5 text-start align-middle shadow-[0_40px_120px_-40px_rgba(0,0,0,0.95)]';

// 38px is FIELD_INPUT once it lands: SIZE_SM's py-2 plus a 20px text line box plus 2px of border.
const FIELD_BAR = `${BONE} h-[38px] w-full rounded-[10px]`;

// The five fields present on every path through the real form. The country-specific field sits between
// the two groups, gated on the same condition the real one uses.
const FIELDS_BEFORE_COUNTRY = ['login.placeholder.name', 'login.placeholder.email', 'login.placeholder.repeat_email'];
const FIELDS_AFTER_COUNTRY = ['login.placeholder.password', 'login.placeholder.repeat_password'];

// The two countries that get an extra field in the real form. Every other country gets neither.
const COUNTRY_FIELD_KEYS: Partial<Record<CountryCodeEnum, string>> = {
  [CountryCodeEnum.BRAZIL]: 'login.placeholder.phone',
  [CountryCodeEnum.RUSSIAN]: 'login.placeholder.telegram',
};

/**
 * Placeholder for the lazily loaded SignUpModal, with the real panel's geometry.
 *
 * It exists because clicking "create account" inside the sign-in modal closes that modal
 * (sign-in-modal.tsx `openModalSingUp` calls setIsOpen(false)) while the sign-up chunk is still in
 * flight. Without a placeholder the user's modal simply vanishes and they are dropped back on the page
 * with nothing in its place.
 *
 * Shows the real title and the real labels — the `common` namespace is already loaded by the parent, so a
 * grey bar over text we already have would be a gratuitous lie and would cost line-box accuracy. Only the
 * inputs, whose content we cannot know, are bones.
 */
export function SignUpModalSkeleton() {
  const { t } = useTranslation('common');
  const router = useRouter();
  const countryCodeUser = useCountryCode();

  // Mirrors the two country branches of the real form. The Brazilian branch lands at 24px of content
  // (containerStyle pins the height and .flag-dropdown is absolute) and the Russian one at 35px (the
  // library's `.react-tel-input .form-control`). Plus 18px for PHONE_FIELD_WRAP's py-2 and border.
  const countryFieldKey = COUNTRY_FIELD_KEYS[countryCodeUser];
  const countryFieldHeight = countryCodeUser === CountryCodeEnum.RUSSIAN ? 'h-[53px]' : 'h-[42px]';

  // Mirrors the real panel's bottom block. `trial?.customTrial` is a prop and next/dynamic cannot pass
  // props to `loading`, so only the route half of that condition is available here.
  const showAlreadyRegistered = router.pathname !== '/affiliates';

  return (
    <div role='status' className='relative z-[1001]' data-testid='sign-up-modal-skeleton' data-np-modal-surface=''>
      {/* No aria-busy here on purpose: on a live region it defers the announcement until it flips to
          false, and this node is unmounted instead of ever flipping. */}
      <span className='sr-only'>{t('login.submitting')}</span>
      {/* No fade: the whole point is to exist on frame 1. Identical to the real overlay, so it covers the
          sign-in overlay that is leaving without a visible seam. */}
      <div className='fixed inset-0 bg-black/60 backdrop-blur-sm' />
      <div className='fixed inset-0 z-50 overflow-y-auto'>
        <div className={`flex min-h-full flex-col items-center justify-center gap-5 p-4 text-center ${bai_jamjuree.className}`}>
          <div className={PANEL}>
            <div className='flex items-start justify-between gap-3'>
              <h3 className='text-xl font-black text-[var(--np-fg)]'>{t('login.create_account')}</h3>
              {/* A bone, not a <button>: `loading` never receives closeModalSingUp, and a dead close
                  button is a lie. The escape hatch for a broken chunk is the `error` guard at the call site. */}
              <div aria-hidden='true' className={`${BONE} size-9 shrink-0 rounded-lg`} />
            </div>
            {/* No SSO block on purpose: useFeatureToggle starts disabled and SsoProviders returns null
                until the toggle request resolves, so the real modal has no SSO on its first frame either. */}
            <div className='flex flex-col gap-5'>
              {FIELDS_BEFORE_COUNTRY.map((labelKey) => (
                <div key={labelKey} className='grid gap-2 text-start'>
                  <span className={FIELD_LABEL}>{t(labelKey)}</span>
                  <div aria-hidden='true' className={FIELD_BAR} />
                </div>
              ))}
              {countryFieldKey && (
                <div className='grid gap-2 text-start'>
                  <span className={FIELD_LABEL}>{t(countryFieldKey)}</span>
                  <div aria-hidden='true' className={`${BONE} w-full rounded-[10px] ${countryFieldHeight}`} />
                </div>
              )}
              {FIELDS_AFTER_COUNTRY.map((labelKey) => (
                <div key={labelKey} className='grid gap-2 text-start'>
                  <span className={FIELD_LABEL}>{t(labelKey)}</span>
                  <div aria-hidden='true' className={FIELD_BAR} />
                </div>
              ))}
              {/* Captcha reservation: 78px is reCAPTCHA's normal size (304x78). Left empty on purpose —
                  drawing a fake "I am not a robot" box would be imitating a third party's trust UI. */}
              <div aria-hidden='true' className='h-[78px]' />
              <div aria-hidden='true' className={`${BONE} h-9 w-full rounded-[10px]`} />
              {showAlreadyRegistered && (
                <div className='flex flex-col gap-5 border-t border-white/10 pt-5'>
                  <h3 className='text-lg font-black text-[var(--np-fg)]'>{t('login.already_registered')}</h3>
                  <div aria-hidden='true' className={`${BONE} h-9 w-full rounded-[10px]`} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
